import { useEffect, useState, useMemo } from 'react';
import { apiRequest } from '@/lib/queryClient';
import { Loader2, GripVertical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { formatDate } from '@/lib/format';

interface Plot {
  id: string;
  name?: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
  thumbnailUrl?: string;
}

interface PlotLibraryProps {
  projectId: string;
}

export function PlotLibrary({ projectId }: PlotLibraryProps) {
  const [plots, setPlots] = useState<Plot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!projectId) return;

    setLoading(true);
    setError(null);
    apiRequest('GET', `/api/projects/${projectId}/plots`)
      .then((res) => res.json())
      .then((data: Plot[]) => {
        setPlots(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load plots:', err);
        setError('Failed to load plots');
        setLoading(false);
      });
  }, [projectId]);

  const filteredPlots = useMemo(() => {
    if (!debouncedSearch.trim()) return plots;
    const query = debouncedSearch.toLowerCase();
    return plots.filter((plot) => {
      const name = (plot.name || plot.title || '').toLowerCase();
      return name.includes(query);
    });
  }, [plots, debouncedSearch]);

  const handleDragStart = (e: React.DragEvent, plot: Plot) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'plot',
      plotId: plot.id,
    }));
    // Visual feedback: reduce opacity on the dragged element
    const target = e.currentTarget as HTMLElement;
    if (target) {
      target.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    // Restore opacity
    const target = e.currentTarget as HTMLElement;
    if (target) {
      target.style.opacity = '1';
    }
  };

  return (
    <div className="h-full flex flex-col p-3">
      {/* Search */}
      <div className="mb-3">
        <Input
          placeholder="Search plots..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-8 text-sm"
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {error && (
          <div className="text-sm text-destructive py-4 px-2">{error}</div>
        )}

        {!loading && !error && filteredPlots.length === 0 && (
          <div className="text-sm text-muted-foreground py-8 px-2 text-center">
            {searchQuery ? 'No plots match your search.' : 'No plots yet. Create a plot in the gallery, then add it here.'}
          </div>
        )}

        {!loading && !error && filteredPlots.length > 0 && (
          <div className="space-y-2">
            {filteredPlots.map((plot) => {
              const plotName = plot.name || plot.title || 'Untitled Plot';
              return (
                <Card
                  key={plot.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, plot)}
                  onDragEnd={handleDragEnd}
                  className="p-3 min-h-[100px] cursor-move hover:shadow-md transition-shadow group"
                  data-testid={`plot-item-${plot.id}`}
                >
                  <div className="flex items-start gap-3">
                    <GripVertical className="h-4 w-4 text-muted-foreground mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium line-clamp-3">{plotName}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        Updated {formatDate(plot.updatedAt)}
                      </div>
                    </div>
                    {plot.thumbnailUrl && (
                      <img
                        src={plot.thumbnailUrl}
                        alt={plotName}
                        className="w-32 h-20 object-cover rounded flex-shrink-0"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

